"use client"

import { createContext, useContext, type ReactNode } from "react"
import { useCountry } from "@/components/layout/country-provider"
import type { Country } from "@/i18n/config"
import type { GateId } from "@/lib/country-gates"
import {
  resolveSectionVisibility,
  type SectionVisibilityOverrides,
} from "@/lib/section-visibility"

/**
 * CMS per-country section overrides, resolved once in
 * `[country]/[locale]/layout.tsx`. Empty (= code registry) wherever no
 * provider is mounted, e.g. isolated component tests.
 */
const SectionVisibilityContext = createContext<SectionVisibilityOverrides>({})

export function SectionVisibilityProvider({
  value,
  children,
}: {
  value: SectionVisibilityOverrides
  children: ReactNode
}) {
  return (
    <SectionVisibilityContext.Provider value={value}>
      {children}
    </SectionVisibilityContext.Provider>
  )
}

/** Client-side `isAllowedInCountry` that honours the CMS overrides. */
export function useIsAllowedInCountry(id: GateId): boolean {
  const { code } = useCountry()
  return resolveSectionVisibility(
    id,
    code,
    useContext(SectionVisibilityContext)
  )
}

/**
 * Same signature as `isAllowedInCountry`, but honours the CMS overrides. For
 * components that gate several sections (or compute the answer inline).
 */
export function useSectionGate(): (id: GateId, country: Country) => boolean {
  const overrides = useContext(SectionVisibilityContext)
  return (id, country) => resolveSectionVisibility(id, country, overrides)
}
